import React, { useState, useEffect } from 'react';
import ConditionBuilder from './ConditionBuilder';
import SessionHistoryModal from './SessionHistoryModal';
import {
  Settings,
  Bot,
  MessageSquare,
  GitBranch,
  Save,
  X,
  Plus,
  Trash2,
  AlertCircle,
  Brain,
  RefreshCw,
  Lock,
  MapPin,
  Eye,
  ChevronDown,
  Check
} from 'lucide-react';
import { Agent, Model, WorkflowStep, Condition } from '../../types';
import CustomSelect from '../common/CustomSelect';
import { useFlowStore } from '../../store';
import useSessionUtilizationStore from '../../store/sessionUtilizationStore';

interface PropertiesPanelProps {
  selectedNode: string | null;
  selectedEdge: string | null;
  agents: Agent[];
  models: Model[];
}

const PropertiesPanel: React.FC<PropertiesPanelProps> = ({
  selectedNode,
  selectedEdge,
  agents,
  models,
}) => {
  console.log('[PropertiesPanel] Rendered');
  console.log('  selectedNode:', selectedNode);
  console.log('  selectedEdge:', selectedEdge);

  const { nodes, edges } = useFlowStore(); // Ensure nodes is destructured
  const { sessionInfos, loadSessionInfo } = useSessionUtilizationStore();
  const [showSessionHistory, setShowSessionHistory] = useState(false);
  const [selectedSessionKey, setSelectedSessionKey] = useState<string | null>(null);

  const selectedNodeData = selectedNode ? nodes.find((n: any) => n.id === selectedNode) : null; // Explicitly type n
  const selectedEdgeData = selectedEdge ? edges.find((e: any) => e.id === selectedEdge) : null; // Explicitly type e

  // Logic to determine the previous step for conditional builder in PropertiesPanel
  const previousStepForConditional = React.useMemo(() => {
    console.log('[PropertiesPanel] Calculating previousStepForConditional...');
    let foundPreviousStep = null;

    if (selectedNodeData && selectedNodeData.data?.type === 'conditional') {
      console.log('  Selected node is conditional:', selectedNodeData.id);
      // Find the incoming edge to the currently selected node
      const incomingEdge = edges.find(edge => edge.target === selectedNodeData.id);
      console.log('    Incoming edge:', incomingEdge);
      if (incomingEdge) {
        const sourceNode = nodes.find(node => node.id === incomingEdge.source);
        console.log('    Source node:', sourceNode);
        console.log('    Source node data step:', sourceNode?.data?.step);
        console.log('    Source node step type:', sourceNode?.data?.step?.step_type);
        // Check if the direct predecessor is an agent step (with output model)
        if (sourceNode?.data?.step?.step_type === 'run_agent') {
          foundPreviousStep = sourceNode.data.step;
        }
      }
    }
    console.log('  Final previousStepForConditional:', foundPreviousStep);
    return foundPreviousStep;
  }, [selectedNodeData, nodes, edges]);

  const [formData, setFormData] = useState<any>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showExecutionModeDropdown, setShowExecutionModeDropdown] = useState(false);
  const [showAgentDropdown, setShowAgentDropdown] = useState(false);
  const [agentSearchQuery, setAgentSearchQuery] = useState('');

  // Utility to normalize conditions (string or object) to {field, operator, value} objects
  function normalizeConditions(rawConditions: any[]): Condition[] {
    if (!rawConditions || !Array.isArray(rawConditions)) return [];
    return rawConditions.map((cond: any) => {
      if (typeof cond === 'object' && 'field' in cond) {
        return cond;
      }
      if (typeof cond === 'string') {
        const trimmedCond = cond.trim();
        if (!trimmedCond) return { field: '', operator: '', value: '' };
        const operators = ['==', '!=', '>=', '<=', '>', '<'];
        let foundOperator = '';
        let operatorIndex = -1;
        for (const op of operators) {
          const index = trimmedCond.indexOf(op);
          if (index !== -1) {
            foundOperator = op;
            operatorIndex = index;
            break;
          }
        }
        if (!foundOperator || operatorIndex === -1) {
          const parts = trimmedCond.split(/\s+/);
          if (parts.length >= 3) {
            const operator = parts[1];
            const field = parts[0];
            const value = parts.slice(2).join(' ').replace(/^['"](.*)['"]$/, '$1');
            return { field, operator, value };
          }
          return { field: '', operator: '', value: '' };
        }
        const field = trimmedCond.slice(0, operatorIndex).trim();
        const valueStart = operatorIndex + foundOperator.length;
        let value = trimmedCond.slice(valueStart).trim();
        if ((value.startsWith('"') && value.endsWith('"')) || (value.startsWith("'") && value.endsWith("'"))) {
          value = value.slice(1, -1);
        }
        return { field, operator: foundOperator, value };
      }
      return { field: '', operator: '', value: '' };
    });
  }

  // Initialize form data when selection changes
  useEffect(() => {
    if (selectedNodeData) {
      // If conditional node, normalize conditions
      if (selectedNodeData.data?.type === 'conditional' && selectedNodeData.data?.condition?.conditions) {
        setFormData({
          ...selectedNodeData.data,
          condition: {
            ...selectedNodeData.data.condition,
            conditions: normalizeConditions(selectedNodeData.data.condition.conditions),
          },
        });
      } else {
        setFormData(selectedNodeData.data || {});
      }
    } else if (selectedEdgeData) {
      setFormData(selectedEdgeData.data || {});
    } else {
      setFormData({});
    }
    setErrors({});
  }, [selectedNodeData, selectedEdgeData]);

  // Load session info when a node with session config is selected
  useEffect(() => {
    if (selectedNodeData?.data?.step?.session_config?.enabled && 
        selectedNodeData?.data?.step?.session_config?.mode === 'shared' &&
        selectedNodeData?.data?.step?.orchestration_id) {
      const sessionKey = `orch_${selectedNodeData.data.step.orchestration_id}`;
      if (!sessionInfos[sessionKey]) {
        loadSessionInfo(sessionKey);
      }
    }
  }, [selectedNodeData, sessionInfos, loadSessionInfo]);

  // Effect to initialize conditions if selected node is conditional and there are available fields
  useEffect(() => {
    if (selectedNodeData?.data?.type === 'conditional') {
      // Re-evaluate available fields here, similar to ConditionBuilder
      const getAvailableFields = () => {
        if (!previousStepForConditional) return {};
        
        if (previousStepForConditional.step_type === 'run_agent') {
          const previousNode = nodes.find(node => node.id === String(previousStepForConditional.orchestration_step_id));
          const agent = previousNode?.data?.agent;
          
          if (agent?.output_model?.properties) {
            return agent.output_model.properties;
          }
        }
        
        if (previousStepForConditional.output_model?.properties) {
          return previousStepForConditional.output_model.properties;
        }
        
        return {};
      };

      const currentAvailableFields = getAvailableFields();

      if (Object.keys(currentAvailableFields).length > 0 && (!formData.condition?.conditions || formData.condition.conditions.length === 0)) {
        setFormData((prev: any) => ({ // Explicitly type prev as any
          ...prev,
          condition: {
            conditions: [{ field: '', operator: '', value: '' }],
          },
        }));
      }
    }
  }, [selectedNodeData, previousStepForConditional, nodes, formData.condition?.conditions]);

  // Handle form field changes
  const handleFieldChange = (field: string, value: any) => {
    setFormData((prev: any) => ({ // Explicitly type prev as any
      ...prev,
      [field]: value,
    }));
    
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Handle nested field changes (for objects)
  const handleNestedFieldChange = (parentField: string, childField: string, value: any) => {
    setFormData((prev: any) => ({ // Explicitly type prev as any
      ...prev,
      [parentField]: {
        ...prev[parentField],
        [childField]: value,
      },
    }));
  };

  // Validate form data
  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (selectedNodeData?.data?.type === 'user_prompt') {
      if (!formData.input_message?.trim()) {
        newErrors.input_message = 'Input message is required';
      }
    } else if (selectedNodeData?.data?.type === 'agent') {
      if (!formData.agentId) {
        newErrors.agentId = 'Agent selection is required';
      }
    } else if (selectedNodeData?.data?.type === 'conditional') {
      if (!formData.condition?.conditions?.length) {
        newErrors.condition = 'At least one condition is required';
      } else {
        // Validate each condition is non-empty
        const emptyConditions = formData.condition.conditions
          .map((c: Condition, i: number) => (!c.field || !c.operator || c.value === '' ? i : -1))
          .filter((i: number) => i !== -1);
        
        if (emptyConditions.length > 0) {
          newErrors.condition = `Conditions at positions ${emptyConditions.join(', ')} cannot be empty`;
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Save changes
  const handleSave = () => {
    if (!validateForm()) return;

    if (selectedNode) {
      // TODO: Implement updateNode in useFlowStore
      // updateNode(selectedNode, { data: formData });
    } else if (selectedEdge) {
      // TODO: Implement updateEdge in useFlowStore
      // updateEdge(selectedEdge, { data: formData });
    }
  };

  // Add condition to conditional node
  const addCondition = () => {
    const conditions = formData.condition?.conditions || [];
    handleNestedFieldChange('condition', 'conditions', [...conditions, '']);
  };

  // Remove condition from conditional node
  const removeCondition = (index: number) => {
    const conditions = formData.condition?.conditions || [];
    const newConditions = conditions.filter((_: any, i: number) => i !== index);
    handleNestedFieldChange('condition', 'conditions', newConditions);
  };

  // Update condition value
  const updateCondition = (index: number, value: string) => {
    const conditions = [...(formData.condition?.conditions || [])];
    conditions[index] = value;
    handleNestedFieldChange('condition', 'conditions', conditions);
  };

  if (!selectedNodeData && !selectedEdgeData) {
    return (
      <div className="h-full flex flex-col">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-warm-white flex items-center">
            <Settings className="w-5 h-5 mr-2" />
            Properties
          </h2>
        </div>
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="text-center">
            <Settings className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Select a node or edge to view its properties
            </p>
          </div>
        </div>
      </div>
    );
  }

  const renderNodeProperties = () => {
    const nodeType = selectedNodeData?.data?.type;

    switch (nodeType) {
      case 'user_prompt':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Prompt Message
              </label>
              <textarea
                value={formData.input_message || ''}
                onChange={(e) => handleFieldChange('input_message', e.target.value)}
                className={`textarea ${errors.input_message ? 'border-red-300' : ''}`}
                rows={3}
                placeholder="Enter the message to display to the user..."
              />
              {errors.input_message && (
                <p className="text-sm text-red-600 mt-1 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.input_message}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Description
              </label>
              <input
                type="text"
                value={formData.description || ''}
                onChange={(e) => handleFieldChange('description', e.target.value)}
                className="input"
                placeholder="Optional description for this step"
              />
            </div>
          </div>
        );

      case 'agent':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Agent
              </label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => { setShowAgentDropdown(!showAgentDropdown); setAgentSearchQuery(''); }}
                  className={`w-full flex items-center justify-between px-3 py-2 text-sm text-left border ${errors.agentId ? 'border-red-300' : 'border-gray-300 dark:border-gray-600'} rounded-md bg-white dark:bg-gray-800 dark:text-warm-white shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500`}
                >
                  <span className={formData.agentId ? '' : 'text-gray-400'}>
                    {formData.agentId
                      ? agents.find(a => String(a.agent_id) === String(formData.agentId))?.name || 'Select an agent...'
                      : 'Select an agent...'}
                  </span>
                  <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showAgentDropdown ? 'rotate-180' : ''}`} />
                </button>
                {showAgentDropdown && (
                  <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-auto">
                    {agents.length > 5 && (
                      <div className="sticky top-0 bg-white dark:bg-gray-800 p-2 border-b border-gray-100 dark:border-gray-700">
                        <input
                          type="text"
                          value={agentSearchQuery}
                          onChange={(e) => setAgentSearchQuery(e.target.value)}
                          placeholder="Search agents..."
                          className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 dark:text-warm-white focus:outline-none focus:ring-1 focus:ring-primary-500"
                          autoFocus
                        />
                      </div>
                    )}
                    {agents
                      .filter(a => !agentSearchQuery || a.name.toLowerCase().includes(agentSearchQuery.toLowerCase()))
                      .map((agent) => (
                        <button
                          key={agent.agent_id}
                          type="button"
                          onClick={() => {
                            handleFieldChange('agentId', String(agent.agent_id));
                            setShowAgentDropdown(false);
                          }}
                          className={`w-full text-left px-3 py-2 text-sm hover:bg-primary-50 dark:hover:bg-primary-900/20 ${
                            String(formData.agentId) === String(agent.agent_id)
                              ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                              : 'text-gray-700 dark:text-gray-300'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span>{agent.name}</span>
                            {String(formData.agentId) === String(agent.agent_id) && (
                              <Check className="w-4 h-4 text-primary-600" />
                            )}
                          </div>
                        </button>
                      ))}
                    {agents.filter(a => !agentSearchQuery || a.name.toLowerCase().includes(agentSearchQuery.toLowerCase())).length === 0 && (
                      <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">No agents found</div>
                    )}
                  </div>
                )}
              </div>
              {errors.agentId && (
                <p className="text-sm text-red-600 mt-1 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.agentId}
                </p>
              )}
            </div>

            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.show_output_message_items || false}
                  onChange={(e) => handleFieldChange('show_output_message_items', e.target.checked)}
                  className="rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  Show output message items
                </span>
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Execution Mode
              </label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowExecutionModeDropdown(!showExecutionModeDropdown)}
                  onBlur={() => setTimeout(() => setShowExecutionModeDropdown(false), 150)}
                  className="w-full flex items-center justify-between px-3 py-2 text-sm text-left border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 dark:text-warm-white shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <span>{(formData.execution_mode || 'synchronous') === 'synchronous' ? 'Synchronous' : 'Parallel'}</span>
                  <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showExecutionModeDropdown ? 'rotate-180' : ''}`} />
                </button>
                {showExecutionModeDropdown && (
                  <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg">
                    {[
                      { value: 'synchronous', label: 'Synchronous', desc: 'Steps run one after another in sequence.' },
                      { value: 'parallel', label: 'Parallel', desc: 'Steps can run simultaneously for better performance.' }
                    ].map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => {
                          const newMode = option.value as 'synchronous' | 'parallel';
                          handleFieldChange('execution_mode', newMode);
                          if (newMode === 'synchronous') {
                            handleFieldChange('fan_out', false);
                          }
                          setShowExecutionModeDropdown(false);
                        }}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-primary-50 dark:hover:bg-primary-900/20 first:rounded-t-md last:rounded-b-md ${
                          (formData.execution_mode || 'synchronous') === option.value
                            ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                            : 'text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium">{option.label}</span>
                          {(formData.execution_mode || 'synchronous') === option.value && (
                            <Check className="w-4 h-4 text-primary-600" />
                          )}
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{option.desc}</p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Fan-out Configuration - only show when execution_mode is parallel */}
            {formData.execution_mode === 'parallel' && (
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.fan_out || false}
                    onChange={(e) => handleFieldChange('fan_out', e.target.checked)}
                    className="rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                    Enable Fan-out
                  </span>
                </label>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  When enabled, the agent will process multiple items in parallel. 
                  This is useful for processing lists of data or running multiple similar tasks simultaneously.
                </p>
              </div>
            )}

            {/* Session Status Display */}
            {selectedNodeData?.data?.step?.session_config?.enabled && (
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                    <Brain className="w-4 h-4 mr-1" />
                    Session Memory
                  </label>
                  <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700">
                    ON
                  </span>
                </div>
                
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Mode:</span>
                    <div className="flex items-center gap-1">
                      {selectedNodeData.data.step.session_config.mode === 'shared' && <RefreshCw className="w-3 h-3" />}
                      {selectedNodeData.data.step.session_config.mode === 'isolated' && <Lock className="w-3 h-3" />}
                      {selectedNodeData.data.step.session_config.mode === 'run' && <MapPin className="w-3 h-3" />}
                      <span className="font-medium capitalize">{selectedNodeData.data.step.session_config.mode}</span>
                    </div>
                  </div>
                  
                  {(() => {
                    const sessionKey = `orch_${selectedNodeData.data.step?.orchestration_id}`;
                    const sessionInfo = sessionInfos[sessionKey];
                    
                    if (sessionInfo) {
                      return (
                        <>
                          <div className="flex items-center justify-between">
                            <span className="text-gray-600 dark:text-gray-400">Context:</span>
                            <span className="font-medium">{sessionInfo.message_count} messages</span>
                          </div>
                          <button
                            onClick={() => {
                              setSelectedSessionKey(sessionKey);
                              setShowSessionHistory(true);
                            }}
                            className="w-full mt-2 px-3 py-1 text-xs bg-primary-50 text-primary-700 rounded hover:bg-primary-100 transition-colors flex items-center justify-center gap-1"
                          >
                            <Eye className="w-3 h-3" />
                            View History
                          </button>
                        </>
                      );
                    } else {
                      return (
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          No active session
                        </div>
                      );
                    }
                  })()}
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Description
              </label>
              <input
                type="text"
                value={formData.description || ''}
                onChange={(e) => handleFieldChange('description', e.target.value)}
                className="input"
                placeholder="Optional description for this step"
              />
            </div>
          </div>
        );

      case 'conditional':
        return (
          <div className="space-y-4">
            <ConditionBuilder
              conditions={formData.condition?.conditions || []}
              onChange={(newConditions: Condition[]) =>
                handleNestedFieldChange('condition', 'conditions', newConditions)
              }
              previousStep={previousStepForConditional}
            />
            {errors.condition && (
              <p className="text-sm text-red-600 mt-1 flex items-center">
                <AlertCircle className="w-4 h-4 mr-1" />
                {errors.condition}
              </p>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  On True Action
                </label>
                <CustomSelect
                  value={formData.on_true?.action || 'continue'}
                  onChange={(value) => handleNestedFieldChange('on_true', 'action', value)}
                  options={[
                    { value: 'continue', label: 'Continue' },
                    { value: 'exit', label: 'Exit' },
                    { value: 'feedback_loop', label: 'Feedback Loop' },
                  ]}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  On False Action
                </label>
                <CustomSelect
                  value={formData.on_false?.action || 'exit'}
                  onChange={(value) => handleNestedFieldChange('on_false', 'action', value)}
                  options={[
                    { value: 'continue', label: 'Continue' },
                    { value: 'exit', label: 'Exit' },
                    { value: 'feedback_loop', label: 'Feedback Loop' },
                  ]}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Description
              </label>
              <input
                type="text"
                value={formData.description || ''}
                onChange={(e) => handleFieldChange('description', e.target.value)}
                className="input"
                placeholder="Optional description for this step"
              />
            </div>
          </div>
        );

      default:
        return (
          <div className="text-center py-8">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No properties available for this node type
            </p>
          </div>
        );
    }
  };

  const renderEdgeProperties = () => {
    return (
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Label
          </label>
          <input
            type="text"
            value={formData.label || ''}
            onChange={(e) => handleFieldChange('label', e.target.value)}
            className="input"
            placeholder="Optional edge label"
          />
        </div>

        <div>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formData.isConditional || false}
              onChange={(e) => handleFieldChange('isConditional', e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Conditional edge
            </span>
          </label>
        </div>

        {formData.isConditional && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Condition
            </label>
            <input
              type="text"
              value={formData.condition || ''}
              onChange={(e) => handleFieldChange('condition', e.target.value)}
              className="input"
              placeholder="Condition expression"
            />
          </div>
        )}
      </div>
    );
  };

  const getIcon = () => {
    if (selectedNodeData) {
      const nodeType = selectedNodeData.data?.type;
      switch (nodeType) {
        case 'agent':
          return <Bot className="w-5 h-5 mr-2" />;
        case 'user_prompt':
          return <MessageSquare className="w-5 h-5 mr-2" />;
        case 'conditional':
          return <GitBranch className="w-5 h-5 mr-2" />;
        default:
          return <Settings className="w-5 h-5 mr-2" />;
      }
    }
    return <Settings className="w-5 h-5 mr-2" />;
  };

  const getTitle = () => {
    if (selectedNodeData) {
      return selectedNodeData.data?.label || 'Node Properties';
    }
    if (selectedEdgeData) {
      return 'Edge Properties';
    }
    return 'Properties';
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-warm-white flex items-center">
          {getIcon()}
          {getTitle()}
        </h2>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {selectedNodeData ? renderNodeProperties() : renderEdgeProperties()}
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
        <div className="flex space-x-2">
          <button
            onClick={handleSave}
            className="btn btn-primary btn-sm flex-1"
          >
            <Save className="w-4 h-4 mr-1" />
            Save Changes
          </button>
          <button
            onClick={() => {
              setFormData(selectedNodeData?.data || selectedEdgeData?.data || {});
              setErrors({});
            }}
            className="btn btn-outline btn-sm"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Session History Modal */}
      {showSessionHistory && selectedSessionKey && (
        <SessionHistoryModal
          open={showSessionHistory}
          onClose={() => {
            setShowSessionHistory(false);
            setSelectedSessionKey(null);
          }}
          sessionKey={selectedSessionKey}
          stepLabel={selectedNodeData?.data?.label || 'Step'}
          mode={selectedNodeData?.data?.step?.session_config?.mode}
        />
      )}
    </div>
  );
};

export default PropertiesPanel;

// Helper function to construct condition string (copied from StepConfigurationModal for use here)
const constructConditionString = (condition: Condition): string => {
  const { field, operator, value } = condition;
  if (!field || !operator) return ''; // Incomplete condition

  let formattedValue = value;
  if (typeof value === 'string') {
    formattedValue = `"${value}"`;
  } else if (typeof value === 'boolean') {
    formattedValue = String(value); // "true" or "false"
  }

  return `${field} ${operator} ${formattedValue}`;
};